import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from '@bonusly/design-system';
import { useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { NotificationCategory, NotificationChannel, NotificationSettings } from './types';
import {
	getSetting,
	isCategoryChannelChecked,
	isCategoryChannelIndeterminate,
	toggleCategoryChannel,
	toggleSetting,
} from './utils/notification-settings-form-toggling';
import { NOTIFICATION_CONFIG } from './utils/notification-settings-mapper';

const EMAIL_CHANNEL: NotificationChannel = 'email';
const SLACK_CHANNEL: NotificationChannel = 'slack';
const MICROSOFT_TEAMS_CHANNEL: NotificationChannel = 'microsoftTeams';

interface NotificationsSettingsFormProps {
	settings: NotificationSettings;
	onChange: (settings: NotificationSettings) => void;
	isDisabled?: boolean;
}

export const NotificationsSettingsForm = ({ settings, onChange, isDisabled }: NotificationsSettingsFormProps) => {
	const { t } = useTranslation('user_settings');
	const user = useCurrentUser();
	const company = useCurrentCompany();
	const hasSlackIntegration = company?.chat_integrations?.has_slack_integration ?? false;
	const hasTeamsIntegration = company?.chat_integrations?.has_ms_teams_integration ?? false;

	// Channels to display in the table (order matters for UI)
	const availableChannels = useMemo(() => {
		const channels: NotificationChannel[] = [EMAIL_CHANNEL];
		if (hasSlackIntegration) channels.push(SLACK_CHANNEL);
		if (hasTeamsIntegration) channels.push(MICROSOFT_TEAMS_CHANNEL);
		return channels;
	}, [hasSlackIntegration, hasTeamsIntegration]);

	// Filter categories and topics based on user role visibility rules
	const visibleCategories = useMemo((): NotificationCategory[] => {
		return NOTIFICATION_CONFIG.categories
			.map((category) => ({
				...category,
				topics: category.topics.filter((topic) => {
					// If no visibleWhen rule, show to all users
					if (!topic.visibleWhen) return true;
					return topic.visibleWhen(user);
				}),
			}))
			.filter((category) => category.topics.length > 0);
	}, [user]);

	// Toggle individual setting
	const handleToggleSetting = (topicId: string, channel: NotificationChannel) => {
		const newSettings = toggleSetting(settings, topicId, channel);
		onChange(newSettings);
	};

	// Toggle all settings in a category for a specific channel (only visible topics)
	const handleToggleCategory = (categoryId: string, channel: NotificationChannel) => {
		const newSettings = toggleCategoryChannel(settings, categoryId, channel, visibleCategories);
		onChange(newSettings);
	};

	return (
		<VStack spacing={0} align="stretch">
			<Table variant="simple" width="100%">
				<Thead>
					<Tr>
						<Th pl={4} borderBottom="1px solid" borderColor="border.soft">
							<Text fontSize="sm" fontWeight="medium" color="text.softer">
								{t('notifications.tableHeader')}
							</Text>
						</Th>

						{availableChannels.map((channel) => (
							<Th key={channel} textAlign="center" borderBottom="1px solid" borderColor="border.soft">
								<Text fontSize="sm" fontWeight="medium" color="text.softer">
									{t(`notifications.channels.${channel}`)}
								</Text>
							</Th>
						))}
					</Tr>
				</Thead>
				<Tbody>
					{visibleCategories.map((category) => (
						<Fragment key={category.id}>
							<Tr>
								<Td pl={4} py={3}>
									<Text fontWeight="semibold" fontSize="sm" color="text.default">
										{t(`notifications.categories.${category.id}.name`)}
									</Text>
								</Td>
								{availableChannels.map((channel) => (
									<Td key={channel} textAlign="center" py={3}>
										<Checkbox
											isChecked={isCategoryChannelChecked(settings, category.id, channel, visibleCategories)}
											isIndeterminate={isCategoryChannelIndeterminate(settings, category.id, channel, visibleCategories)}
											onChange={() => handleToggleCategory(category.id, channel)}
											colorScheme="blue"
											size="md"
											isDisabled={isDisabled}
										/>
									</Td>
								))}
							</Tr>
							{category.topics.map((topic) => (
								<Tr key={topic.id} bg="bg.canvas">
									<Td pl={4} py={3} verticalAlign="top">
										<VStack align="start" spacing={1}>
											<Text fontWeight="medium" fontSize="sm" color="text.default">
												{t(`notifications.topics.${topic.id}.name`)}
											</Text>
											<Text fontSize="xs" color="text.softer" lineHeight="short">
												{t(`notifications.topics.${topic.id}.description`)}
											</Text>
										</VStack>
									</Td>
									{availableChannels.map((channel) => (
										<Td key={channel} textAlign="center" py={3}>
											{topic.availableChannels.includes(channel) && (
												<Checkbox
													isChecked={getSetting(settings, topic.id, channel)}
													onChange={() => handleToggleSetting(topic.id, channel)}
													colorScheme="blue"
													size="md"
													isDisabled={isDisabled}
												/>
											)}
										</Td>
									))}
								</Tr>
							))}
						</Fragment>
					))}
				</Tbody>
			</Table>
		</VStack>
	);
};
